<template>
  <div class="min-h-screen flex items-center justify-center p-8">
    <div class="max-w-sm w-full p-10 rounded-2xl shadow-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center text-center gap-5">
        <MdiLoading class="w-14 h-14 animate-spin text-[color:var(--color-accent)]" />
        <h2 class="text-xl font-bold m-0 text-[color:var(--color-text)]">{{ $t('oidcCallback.completingSignIn') }}</h2>
        <p class="text-sm m-0 text-[color:var(--color-muted)]">{{ $t('oidcCallback.pleaseWait') }}</p>
      </div>

      <!-- Error -->
      <div v-else-if="errorMessage" class="flex flex-col items-center text-center gap-5">
        <MdiCloseCircle class="w-14 h-14 text-red-400" />
        <h2 class="text-xl font-bold m-0 text-[color:var(--color-text)]">{{ $t('oidcCallback.authFailed') }}</h2>
        <p class="text-sm text-red-400 m-0">{{ errorMessage }}</p>
        <button class="btn primary" @click="router.replace('/login')">{{ $t('oidcCallback.tryAgain') }}</button>
      </div>

      <!-- Success -->
      <div v-else class="flex flex-col items-center text-center gap-5">
        <MdiCheckCircle class="w-14 h-14 text-emerald-400" />
        <h2 class="text-xl font-bold m-0 text-[color:var(--color-text)]">{{ $t('oidcCallback.signInSuccessful') }}</h2>
        <p class="text-sm text-emerald-400 m-0">{{ $t('oidcCallback.redirecting') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useI18n } from 'vue-i18n'
import MdiLoading from '~icons/mdi/loading'
import MdiCloseCircle from '~icons/mdi/close-circle'
import MdiCheckCircle from '~icons/mdi/check-circle'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

const loading = ref(true)
const errorMessage = ref(null)

onMounted(async () => {
  const code = route.query.code
  const state = route.query.state

  if (!code || !state) {
    errorMessage.value = t('oidcCallback.missingParams')
    loading.value = false
    return
  }

  try {
    const result = await authStore.handleOIDCCallback(code, state)
    if (result.portal_handoff) {
      sessionStorage.removeItem('portal-oidc-state')
      const destination = new URL(result.portal_url)
      destination.searchParams.set('verify', result.portal_handoff)
      window.location.replace(destination.href)
      return
    }
    loading.value = false
    // Small delay so the success state is visible before redirecting
    const redirect = sessionStorage.getItem('auth_redirect') || '/'
    sessionStorage.removeItem('auth_redirect')
    setTimeout(() => router.replace(redirect), 800)
  } catch (e) {
    const msg = e.data?.error || e.data?.detail || e.message || t('oidcCallback.authFailedGeneric')
    errorMessage.value = msg
    loading.value = false
  }
})
</script>
